import { useState } from "react";
import { TabContext, TabPanel } from "@mui/lab";
import { Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { WaringIcon } from "../../../../assets/icons";
import { AppRoutes } from "../../../../common/routesUtils";
import ContentLocked from "../../../../components/common/ContentLocked";
import ForbiddenTooltip from "../../../../components/common/ForbiddenTooltip";
import { View } from "../../../../components/layout/View";
import { PageType } from "../../../../config/page.types";
import { useBreadcrumbs } from "../../../../context/BreadcrumbsProvider";
import { useFiltersRegistry } from "../../../../context/FiltersProvider";
import { ApiRoutes } from "../../../../datasources/networking/apiRoutes";
import { getLockedContents } from "../../../../permissions/permissionUtils";
import { usePermissions } from "../../../../permissions/usePermissions";
import { AggregateScenario } from "./tabs/AggregateScenario";
import { ChangesFmissionFactors } from "./tabs/ChangesFmissionFactors";
import { ModalShift } from "./tabs/ModalShift";
import { PerimeterVariation } from "./tabs/PerimeterVariation";
import { RootCauseAnalysis } from "./tabs/RootCauseAnalysis";
import { Specifications } from "./tabs/Specifications";
import { TransportQuantity } from "./tabs/TransportQuantity";

type RootCauseTabWarningState = Record<number, string[]>;

export const RootCauseResults = () => {
	const navigate = useNavigate();
	const { visitPage } = useBreadcrumbs();
	const { t } = useTranslation("dataInsights.rootcause");
	const { clearRootCause, rootCauseState } = useFiltersRegistry();
	const { canCall } = usePermissions();

	// a tab is forbidden if the user cannot call its widget with the analysis perspective
	const isTabForbidden = (route: ApiRoutes) => !canCall(route, "POST", rootCauseState.payload.perspective);

	const [value, setValue] = useState(0);

	const [tabWarnings, setTabWarnings] = useState<RootCauseTabWarningState>({});

	const updateTabWarnings = (tabValue: number, warnings?: string[]) => {
		setTabWarnings((prev) => ({
			...prev,
			[tabValue]: warnings ?? [],
		}));
	};

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		if (tabs.find((tab) => tab.value === newValue)?.isForbidden) return;
		setValue(newValue);
	};

	const editRedirect = () => {
		visitPage(PageType.DataInsightsRootCause, false);
		navigate(AppRoutes.DataInsightsRootCause);
	};

	const newAnalysisRedirect = () => {
		visitPage(PageType.DataInsightsRootCause, false);
		navigate(AppRoutes.DataInsightsRootCause);
		clearRootCause();
	};

	const tabs = [
		{ value: 0, label: t("root cause results/tabs/specifications"), content: <Specifications /> },
		{
			value: 1,
			label: t("root cause results/tabs/aggregate scenario results"),
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetAggregate),
			content: <AggregateScenario onWarningsChange={(warnings) => updateTabWarnings(1, warnings)} />,
		},
		{
			value: 2,
			label: t("root cause results/tabs/root cause analysis"),
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetWaterfall),
			content: <RootCauseAnalysis onWarningsChange={(warnings) => updateTabWarnings(2, warnings)} />,
		},
		{
			value: 3,
			label: t("root cause results/tabs/perimeter variation"),
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetPerimeter),
			content: <PerimeterVariation onWarningsChange={(warnings) => updateTabWarnings(3, warnings)} />,
		},
		{
			value: 4,
			label: t("root cause results/tabs/transport quantity"),
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetTransportQuantity),
			content: <TransportQuantity onWarningsChange={(warnings) => updateTabWarnings(4, warnings)} />,
		},
		{
			value: 5,
			label: t("root cause results/tabs/modal shift"),
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetModalShift),
			content: <ModalShift onWarningsChange={(warnings) => updateTabWarnings(5, warnings)} />,
		},
		{
			value: 6,
			label: t("root cause results/tabs/changes in emission factors"),
			// all emission factor calls (total and by transport mode) belong to the same feature
			isForbidden: isTabForbidden(ApiRoutes.RootCauseWidjetEmissionsChangeTotal),
			content: <ChangesFmissionFactors onWarningsChange={(warnings) => updateTabWarnings(6, warnings)} />,
		},
	];

	const lockedContents = getLockedContents(
		tabs.map((tab) => ({ models: [{ isForbidden: !!tab.isForbidden }], names: [tab.label] }))
	);

	return (
		<View
			pageType={PageType.DataInsightsRootCauseResults}
			headerContext={{
				editRedirect,
				newAnalysisRedirect,
			}}
		>
			<TabContext value={value}>
				<Stack direction="row-reverse" width="100%" py={2}>
					<Tabs
						orientation="vertical"
						value={value}
						onChange={handleChange}
						sx={{
							width: 260,
							minWidth: 260,
							maxWidth: 260,
							flexShrink: 0,
							borderLeft: 1,
							borderColor: "divider",
						}}
					>
						{tabs.map((tab) => (
							<Tab
								key={tab.value}
								value={tab.value}
								aria-disabled={tab.isForbidden}
								sx={{
									alignItems: "stretch",
									textAlign: "left",
									// forbidden tabs keep pointer events, so their tooltip can explain why they are locked
									...(tab.isForbidden && {
										opacity: (theme) => theme.palette.action.disabledOpacity,
									}),
								}}
								label={
									<Stack direction="row" gap={1} alignItems="center" width="100%">
										<ForbiddenTooltip
											forbidden={!!tab.isForbidden}
											title={t("common:content locked/content forbidden")}
										>
											<span>{tab.label}</span>
										</ForbiddenTooltip>

										{!!tabWarnings[tab.value]?.length && (
											<Tooltip
												arrow
												placement="left"
												title={
													<Stack gap={0.5}>
														<Typography variant="caption" fontWeight={700}>
															{t("root cause results/warnings/title")}
														</Typography>
														{tabWarnings[tab.value].map((warning, index) => (
															<Typography key={index} variant="caption">
																{warning}
															</Typography>
														))}
													</Stack>
												}
												slotProps={{
													popper: {
														sx: {
															zIndex: 9999,
														},
													},
													tooltip: {
														sx: (theme) => ({
															backgroundColor: theme.palette.background.paper,
															color: theme.palette.text.primary,
															border: `1px solid ${theme.palette.divider}`,
															boxShadow: theme.shadows[3],
															borderRadius: 2,
															p: 1,
															maxWidth: 400,
														}),
													},
													arrow: {
														sx: (theme) => ({
															color: theme.palette.background.paper,
															"&::before": {
																border: `1px solid ${theme.palette.divider}`,
															},
														}),
													},
												}}
											>
												<span style={{ display: "inline-flex" }}>
													<WaringIcon color="warning" />
												</span>
											</Tooltip>
										)}
									</Stack>
								}
							/>
						))}
					</Tabs>

					<Stack
						direction="column"
						pr={2}
						sx={{
							flex: 1,
							minWidth: 0,
						}}
					>
						{tabs.map((tab) => (
							<TabPanel key={tab.value} value={tab.value} sx={{ p: 0 }}>
								<Typography color="primary" variant="h5" mb={1}>
									{tab.label}
								</Typography>

								{tab.content}
							</TabPanel>
						))}
					</Stack>
				</Stack>
			</TabContext>

			<ContentLocked items={lockedContents} />
		</View>
	);
};
